<script setup lang="ts">
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { DocumentsService } from '@/types/generated/services.gen'
  import { ResourceTypeEnum } from '@/types/generated/types.gen'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLocaleStore } from '@/store/localStore'

  interface PreviewDocument {
    uuid: string
    url: string
    title?: string
    filename: string
  }

  interface Props {
    document: PreviewDocument
    height?: string
    allowDownload?: boolean
    allowPrint?: boolean
    showBorder?: boolean
    disablePlugins?: boolean
    token?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    height: '400px',
    allowDownload: false,
    allowPrint: false,
    showBorder: true,
    disablePlugins: false,
    token: undefined
  })

  interface OnlyOfficeInstance {
    destroyEditor: () => void
  }

  const userStore = useUserStore()
  const localeStore = useLocaleStore()
  const containerId = ref(`office-preview-${Math.random().toString(36).substr(2, 9)}`)
  const isLoading = ref(true)
  const hasError = ref(false)
  let editorInstance: OnlyOfficeInstance | null = null
  let pendingTimeout: ReturnType<typeof setTimeout> | null = null

  function getDocumentType(filename: string): { fileType: string; documentType: string } {
    const lower = filename.toLowerCase()

    if (lower.endsWith('.docx') || lower.endsWith('.doc')) {
      return { fileType: lower.endsWith('.docx') ? 'docx' : 'doc', documentType: 'word' }
    } else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
      return { fileType: lower.endsWith('.xlsx') ? 'xlsx' : 'xls', documentType: 'cell' }
    } else if (lower.endsWith('.pptx') || lower.endsWith('.ppt')) {
      return { fileType: lower.endsWith('.pptx') ? 'pptx' : 'ppt', documentType: 'slide' }
    } else if (lower.endsWith('.pdf')) {
      return { fileType: 'pdf', documentType: 'pdf' }
    }

    return { fileType: 'docx', documentType: 'word' }
  }

  async function fetchViewToken(): Promise<string | null> {
    if (!props.document?.uuid) return null
    try {
      const response = await DocumentsService.documentsCreateEditTokenCreate({
        uuid: props.document.uuid,
        requestBody: { resource_type: ResourceTypeEnum.DOCUMENT }
      })
      return response.onlyoffice_jwt
    } catch (error) {
      console.error('Failed to fetch view token for preview:', error)
      return null
    }
  }

  async function initializePreview() {
    if (!props.document?.url) {
      hasError.value = true
      isLoading.value = false
      return
    }

    // Ensure user is loaded
    if (!userStore.user) {
      await userStore.fetchUser()
    }

    // Check if OnlyOffice API is already loaded
    if (!(window as any).DocsAPI) {
      // Load OnlyOffice script if not already loaded
      const existingScript = document.querySelector('script[src*="onlyoffice"]')
      if (!existingScript) {
        const script = document.createElement('script')
        script.src = '/onlyoffice/web-apps/apps/api/documents/api.js'
        script.onload = () =>
          createEditor().catch((err) => {
            console.error('Failed to initialize OnlyOffice editor:', err)
            hasError.value = true
            isLoading.value = false
          })
        script.onerror = () => {
          hasError.value = true
          isLoading.value = false
        }
        document.head.appendChild(script)
      } else {
        // Wait for existing script to load
        pendingTimeout = setTimeout(() => {
          createEditor().catch((err) => {
            console.error('Failed to initialize OnlyOffice editor:', err)
            hasError.value = true
            isLoading.value = false
          })
        }, 100)
      }
    } else {
      createEditor().catch((err) => {
        console.error('Failed to initialize OnlyOffice editor:', err)
        hasError.value = true
        isLoading.value = false
      })
    }
  }

  async function createEditor() {
    if (!(window as any).DocsAPI) {
      pendingTimeout = setTimeout(() => {
        createEditor().catch((err) => {
          console.error('Failed to initialize OnlyOffice editor:', err)
          hasError.value = true
          isLoading.value = false
        })
      }, 100)
      return
    }

    cleanupEditor()

    const { fileType, documentType } = getDocumentType(props.document.filename)

    let url = props.document.url

    const currentLocale = localeStore.getLocale

    let resolvedToken = props.token
    if (!resolvedToken) {
      // Only replace localhost with caddy when fetching our own token,
      // since the self-fetched token will use the internal hostname
      if (url.includes('localhost')) {
        url = url.replace('localhost', 'caddy')
      }
      resolvedToken = (await fetchViewToken()) ?? undefined
    }

    if (!resolvedToken) {
      console.error('No JWT token available for OnlyOffice preview')
      hasError.value = true
      isLoading.value = false
      return
    }

    // When an external JWT is provided, we must build the config to exactly match
    // the JWT's payload. OnlyOffice compares every JWT field against the config and
    // rejects the request if any differ ("token has modified permissions").
    let jwtPayload: any = null
    if (props.token) {
      try {
        const base64url = resolvedToken!.split('.')[1]
        const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
        jwtPayload = JSON.parse(atob(padded))
      } catch {
        // If JWT can't be decoded, fall through to default config
      }
    }

    const config: any = jwtPayload
      ? {
          ...jwtPayload,
          type: 'embedded',
          token: resolvedToken,
          height: props.height,
          width: '100%',
          editorConfig: {
            ...jwtPayload.editorConfig,
            lang: currentLocale,
            region: currentLocale === 'de' ? 'de-DE' : 'en-US',
            customization: {
              compactHeader: true,
              compactToolbar: false,
              toolbar: false,
              hideRightMenu: true,
              hideRulers: true,
              toolbarHideFileName: true,
              toolbarNoTabs: true,
              leftPanel: false,
              features: {
                download: props.allowDownload
              },
              logo: {
                visible: false
              },
              plugins: props.disablePlugins ? false : undefined,
              uiTheme: 'theme-white',
              zoom: -2
            }
          }
        }
      : {
          document: {
            fileType: fileType,
            key: `${props.document.uuid}-inline-${Date.now()}`,
            title: props.document.title || props.document.filename,
            url: url,
            permissions: {
              edit: false,
              download: props.allowDownload,
              print: props.allowPrint
            }
          },
          documentType: documentType,
          editorConfig: {
            mode: 'view',
            lang: currentLocale,
            region: currentLocale === 'de' ? 'de-DE' : 'en-US',
            customization: {
              compactHeader: true,
              compactToolbar: false,
              toolbar: false,
              hideRightMenu: true,
              hideRulers: true,
              toolbarHideFileName: true,
              toolbarNoTabs: true,
              leftPanel: false,
              features: {
                download: props.allowDownload
              },
              logo: {
                visible: false
              },
              plugins: props.disablePlugins ? false : undefined,
              uiTheme: 'theme-white',
              zoom: -2
            }
          },
          type: 'embedded',
          token: resolvedToken,
          height: props.height,
          width: '100%'
        }

    // Add user info only when self-fetching token (external JWT already includes user)
    if (!jwtPayload && userStore.user) {
      config.editorConfig.user = {
        id: userStore.user.uuid,
        name: `${userStore.user.first_name} ${userStore.user.last_name}`
      }
    }

    config.events = {
      onDocumentReady: () => {
        hideToolbarInIframe()
        isLoading.value = false
      }
    }

    observeIframeAndInjectStyles()

    try {
      editorInstance = new (window as any).DocsAPI.DocEditor(containerId.value, config)
    } catch (error) {
      console.error('Failed to create OnlyOffice preview:', error)
      hasError.value = true
      isLoading.value = false
    }
  }

  let iframeObserver: MutationObserver | null = null
  let currentIframe: HTMLIFrameElement | null = null
  let iframeLoadHandler: (() => void) | null = null

  function observeIframeAndInjectStyles() {
    const wrapper = document
      .querySelector(`#${containerId.value}`)
      ?.closest('.office-preview-wrapper')
    if (!wrapper) return

    iframeObserver?.disconnect()
    iframeObserver = new MutationObserver(() => {
      const iframe = wrapper.querySelector('iframe')
      if (!iframe) return
      iframeObserver?.disconnect()

      cleanupIframeListener()
      currentIframe = iframe

      const MAX_INJECT_ATTEMPTS = 50
      let attempts = 0
      const tryInject = () => {
        if (attempts++ >= MAX_INJECT_ATTEMPTS) return
        try {
          if (iframe.contentDocument?.head) {
            injectIframeStyles(iframe)
          } else {
            requestAnimationFrame(tryInject)
          }
        } catch {
          requestAnimationFrame(tryInject)
        }
      }
      tryInject()

      iframeLoadHandler = () => {
        try {
          if (iframe.contentDocument?.head) {
            injectIframeStyles(iframe)
          }
        } catch {
          // Cross-origin — silently ignore
        }
      }
      iframe.addEventListener('load', iframeLoadHandler)
    })

    iframeObserver.observe(wrapper, { childList: true, subtree: true })
  }

  function cleanupIframeListener() {
    if (currentIframe && iframeLoadHandler) {
      currentIframe.removeEventListener('load', iframeLoadHandler)
    }
    currentIframe = null
    iframeLoadHandler = null
  }

  function injectIframeStyles(iframe: HTMLIFrameElement) {
    if (!iframe.contentDocument) return
    // Avoid double-injection
    if (iframe.contentDocument.querySelector('[data-jupus-preview-styles]')) return
    const style = iframe.contentDocument.createElement('style')
    style.setAttribute('data-jupus-preview-styles', 'true')
    style.textContent = `
      .loadmask, .loader-page, .loader-page-romb, .loader-page-text,
      .romb, #loading-mask, .asc-loadmask { display: none !important; }
      #toolbar { display: none !important; }
    `
    iframe.contentDocument.head.appendChild(style)
  }

  function hideToolbarInIframe() {
    try {
      const wrapper = document
        .querySelector(`#${containerId.value}`)
        ?.closest('.office-preview-wrapper')
      const iframe = wrapper?.querySelector('iframe')
      if (iframe) injectIframeStyles(iframe)
    } catch {
      // Cross-origin iframe — cannot access contentDocument
    }
  }

  function cleanupEditor() {
    if (editorInstance) {
      try {
        editorInstance.destroyEditor()
      } catch (e) {
        console.error('Error destroying editor:', e)
      }
      editorInstance = null
    }
  }

  // Watch for document changes
  watch(
    () => props.document,
    () => {
      if (props.document) {
        hasError.value = false
        isLoading.value = true
        initializePreview()
      }
    }
  )

  onMounted(() => {
    initializePreview()
  })

  onUnmounted(() => {
    if (pendingTimeout !== null) clearTimeout(pendingTimeout)
    iframeObserver?.disconnect()
    cleanupIframeListener()
    cleanupEditor()
  })
</script>

<template>
  <div
    class="office-preview-wrapper"
    :class="{ 'with-border': showBorder, 'is-loading': isLoading }"
    :style="{ height: height }"
  >
    <!-- Preview container (always in DOM so OnlyOffice can mount) -->
    <div
      :id="containerId"
      class="preview-embed"
    ></div>

    <!-- Loading overlay (covers iframe while OnlyOffice loads internally) -->
    <div
      v-if="isLoading"
      class="preview-overlay"
    >
      <div class="preview-spinner"></div>
      <span>Loading preview...</span>
    </div>

    <!-- Error state -->
    <div
      v-if="hasError && !isLoading"
      class="preview-overlay preview-error"
    >
      <span>Unable to load preview</span>
    </div>
  </div>
</template>

<style scoped>
  .office-preview-wrapper {
    width: 100%;
    overflow: hidden;
    background: #ffffff;
    position: relative;
  }

  .office-preview-wrapper.with-border {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }

  .preview-embed {
    width: 100%;
    height: 100%;
  }

  /* Hide iframe content until document is fully ready — prevents OnlyOffice
     loading spinner from flashing before our injected CSS can act.
     OnlyOffice replaces the container div, so we target iframe from wrapper. */
  .office-preview-wrapper.is-loading :deep(iframe) {
    visibility: hidden;
  }

  .preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: #666;
    font-size: 0.875rem;
    background: #ffffff;
    z-index: 10;
  }

  .preview-error {
    color: #dc3545;
  }

  .preview-spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #e0e0e0;
    border-top: 3px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>

<!-- Global style to hide OnlyOffice loading mask injected into parent DOM -->
<style>
  #loading-mask.loadmask {
    display: none !important;
  }
</style>
